import { create } from 'zustand';

// ==============================|| BUSINESS CONTEXT - ZUSTAND STORE ||============================== //
// Global store for business logic data: active project, company info, session data, etc.
// All object names are in Spanish to match the database and backend conventions.

const initialState = {
  // Active project data. null when no project is selected.
  // When a user selects a project, this object is populated with its header information,
  // including the associated client ID and client name.
  proyectoActivo: null,

  // Active business unit (unidad de negocio). null when no unit is selected.
  unidadNegocioActiva: null
};

const useBusinessContext = create((set, get) => ({
  // ----- State -----
  ...initialState,

  // ----- Proyecto Activo -----

  /**
   * Set the active project. Pass null to clear, or an object with project data.
   * @param {Object|null} proyecto - Project header data or null
   * @param {string} [proyecto.nombre] - Project name
   * @param {string} [proyecto.estatus] - Project status
   * @param {number} [proyecto.progreso] - Project progress (0-100)
   * @param {number|null} [proyecto.clienteId] - Associated client ID
   * @param {string} [proyecto.clienteNombre] - Associated client name
   */
  setProyectoActivo: (data) => set({
    proyectoActivo: data
      ? {
          id: data.id ?? null,
          nombre: data.nombre || '',
          estatus: data.estatus || '',
          progreso: data.progreso ?? 0,
          clienteId: data.clienteId ?? data.cliente_id ?? null,
          clienteNombre: (data.clienteNombre ?? data.cliente_nombre) || '',
          fechaInicio: (data.fechaInicio ?? data.fecha_inicio) || null,
          // Store the full raw project and client objects as single nested objects
          proyecto: data.proyecto || null,
          cliente: data.cliente || null
        }
      : null
  }),

  /**
   * Get the current active project data.
   * @returns {Object|null}
   */
  getProyectoActivo: () => get().proyectoActivo,

  // ----- Unidad de Negocio Activa -----

  /**
   * Set the active business unit. Pass null to clear.
   * @param {Object|null} unidad - Business unit data
   * @param {number} unidad.id
   * @param {number} unidad.id_proyecto
   * @param {string} unidad.nombre
   * @param {string} unidad.descripcion
   */
  setUnidadNegocioActiva: (data) => set({
    unidadNegocioActiva: data
      ? {
          id: data.id ?? data.id_unidad_negocio ?? null,
          id_proyecto: data.id_proyecto ?? data.idProyecto ?? null,
          nombre: data.nombre || '',
          descripcion: data.descripcion || ''
        }
      : null
  }),

  /**
   * Get the current active business unit data.
   * @returns {Object|null}
   */
  getUnidadNegocioActiva: () => get().unidadNegocioActiva,

  // ----- Reset -----

  /**
   * Reset the entire business context to its initial state.
   * Useful on logout or session clear.
   */
  resetBusinessContext: () => set({ ...initialState })
}));

export default useBusinessContext;
